import React from 'react';
import { useMetaTags } from '../hooks/useMetaTags';
export function PrivacyPolicyPage() {
  useMetaTags({
    title: 'Privacy Policy | FindOnlineDegrees',
    description: 'Privacy policy for FindOnlineDegrees.org'
  });
  return <div className="min-h-screen bg-gray-100" data-id="element-931">
      {/* Hero Section */}
      <section className="w-full bg-[#1a2332] text-white py-16 px-4" data-id="element-932">
        <div className="max-w-4xl mx-auto text-center" data-id="element-933">
          <h1 className="text-3xl md:text-5xl font-bold mb-4" data-id="element-934">
            Privacy Policy
          </h1>
          <p className="text-gray-300 text-sm" data-id="element-935">Last updated: January 2026</p>
        </div>
      </section>

      {/* Content Section */}
      <section className="max-w-4xl mx-auto px-4 py-12" data-id="element-936">
        <div className="bg-white rounded-lg shadow-sm p-8 md:p-12 space-y-8" data-id="element-937">
          <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-938">
            <p data-id="element-939">
              This privacy notice for FindOnlineDegrees.org describes how and
              why we might collect, store, use, and/or share your information
              when you use our services, such as when you:
            </p>
            <ul className="list-disc pl-6 space-y-2" data-id="element-940">
              <li data-id="element-941">
                Visit our website, or any website of ours that links to this
                privacy notice
              </li>
              <li data-id="element-942">
                Engage with us in other related ways, including any sales,
                marketing, or events
              </li>
            </ul>
            <p className="font-semibold" data-id="element-943">
              Questions or concerns? Reading this privacy notice will help you
              understand your privacy rights and choices. If you do not agree
              with our policies and practices, please do not use our services.
            </p>
          </div>

          {/* Summary of Key Points */}
          <div data-id="element-944">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-945">
              SUMMARY OF KEY POINTS
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-946">
              <ul className="list-disc pl-6 space-y-2" data-id="element-947">
                <li data-id="element-948">
                  <strong data-id="element-949">What personal information do we process?</strong> When
                  you visit, use, or navigate our services, we may process
                  personal information depending on how you interact with us,
                  the choices you make, and the features you use.
                </li>
                <li data-id="element-950">
                  <strong data-id="element-951">Do we process sensitive personal information?</strong>{' '}
                  We do not process sensitive personal information.
                </li>
                <li data-id="element-952">
                  <strong data-id="element-953">Do we receive information from third parties?</strong>{' '}
                  We may receive technical and advertising data from third
                  parties, including advertising platforms and analytics
                  providers.
                </li>
                <li data-id="element-954">
                  <strong data-id="element-955">How do we process your information?</strong> To
                  provide, improve, and administer our services, communicate
                  with you, ensure security and fraud prevention, comply with
                  law, and with your consent where required.
                </li>
                <li data-id="element-956">
                  <strong data-id="element-957">
                    When and with whom do we share personal information?
                  </strong>{' '}
                  Only in specific circumstances (e.g., business transfers or
                  service providers).
                </li>
                <li data-id="element-958">
                  <strong data-id="element-959">How do we keep your information safe?</strong> With
                  organizational and technical measures, but no method is 100%
                  secure.
                </li>
                <li data-id="element-960">
                  <strong data-id="element-961">What are your rights?</strong> Depending on your
                  location, you may have rights under applicable privacy laws.
                </li>
                <li data-id="element-962">
                  <strong data-id="element-963">How do you exercise your rights?</strong> You can
                  exercise rights by contacting us (see Section 12).
                </li>
              </ul>
            </div>
          </div>

          {/* Table of Contents */}
          <div data-id="element-964">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-965">
              TABLE OF CONTENTS
            </h2>
            <ol className="list-decimal pl-6 space-y-2 text-gray-700" data-id="element-966">
              <li data-id="element-967">WHAT INFORMATION DO WE COLLECT?</li>
              <li data-id="element-968">HOW DO WE PROCESS YOUR INFORMATION?</li>
              <li data-id="element-969">SMS / TEXT MESSAGE COMMUNICATIONS</li>
              <li data-id="element-970">WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</li>
              <li data-id="element-971">DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</li>
              <li data-id="element-972">HOW LONG DO WE KEEP YOUR INFORMATION?</li>
              <li data-id="element-973">HOW DO WE KEEP YOUR INFORMATION SAFE?</li>
              <li data-id="element-974">DO WE COLLECT INFORMATION FROM MINORS?</li>
              <li data-id="element-975">WHAT ARE YOUR PRIVACY RIGHTS?</li>
              <li data-id="element-976">CONTROLS FOR DO-NOT-TRACK FEATURES</li>
              <li data-id="element-977">DO WE MAKE UPDATES TO THIS NOTICE?</li>
              <li data-id="element-978">HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</li>
            </ol>
          </div>

          {/* 1. What Information Do We Collect */}
          <div data-id="element-979">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-980">
              1. WHAT INFORMATION DO WE COLLECT?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-981">
              <h3 className="font-bold text-lg" data-id="element-982">
                Personal information you disclose to us
              </h3>
              <p data-id="element-983">
                We collect personal information that you voluntarily provide to
                us when you express an interest in obtaining information about
                us or our services, when you participate in activities on the
                services, or otherwise when you contact us.
              </p>
              <p data-id="element-984">
                <strong data-id="element-985">Personal Information Provided by You.</strong> The
                personal information we collect may include:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-986">
                <li data-id="element-987">Names</li>
                <li data-id="element-988">Phone numbers</li>
                <li data-id="element-989">Email addresses</li>
                <li data-id="element-990">Mailing addresses</li>
                <li data-id="element-991">Education level and interests</li>
                <li data-id="element-992">Program preferences</li>
              </ul>

              <h3 className="font-bold text-lg mt-6" data-id="element-993">
                Information automatically collected
              </h3>
              <p data-id="element-994">
                We automatically collect certain information when you visit,
                use, or navigate the services. This information does not reveal
                your specific identity but may include device and usage
                information, such as:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-995">
                <li data-id="element-996">IP address</li>
                <li data-id="element-997">Browser and device characteristics</li>
                <li data-id="element-998">Operating system</li>
                <li data-id="element-999">Language preferences</li>
                <li data-id="element-1000">Referring URLs</li>
                <li data-id="element-1001">Device name and type</li>
                <li data-id="element-1002">Pages viewed and time spent</li>
                <li data-id="element-1003">Clickstream data</li>
              </ul>
            </div>
          </div>

          {/* 2. How Do We Process Your Information */}
          <div data-id="element-1004">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1005">
              2. HOW DO WE PROCESS YOUR INFORMATION?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1006">
              <p data-id="element-1007">
                We process your personal information for a variety of reasons,
                including:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1008">
                <li data-id="element-1009">To facilitate account creation and authentication</li>
                <li data-id="element-1010">To deliver and facilitate delivery of services to you</li>
                <li data-id="element-1011">To respond to user inquiries and offer support</li>
                <li data-id="element-1012">To send administrative information to you</li>
                <li data-id="element-1013">To fulfill and manage your requests</li>
                <li data-id="element-1014">To enable user-to-user communications</li>
                <li data-id="element-1015">
                  To request feedback and contact you about your use of our
                  services
                </li>
                <li data-id="element-1016">To send you marketing and promotional communications</li>
                <li data-id="element-1017">To deliver targeted advertising to you</li>
                <li data-id="element-1018">
                  To protect our services and investigate potential violations
                </li>
                <li data-id="element-1019">
                  To identify usage trends and determine the effectiveness of
                  campaigns
                </li>
                <li data-id="element-1020">To save or protect an individual's vital interest</li>
                <li data-id="element-1021">To comply with our legal obligations</li>
              </ul>

              <h3 className="font-bold text-lg mt-6" data-id="element-1022">Email Communications</h3>
              <p data-id="element-1023">
                When you submit a form on our website, you may consent to
                receive marketing and promotional emails from
                FindOnlineDegrees.org (operated by Media Matchers LLC) and its
                educational partners at the email address you provide. Marketing
                emails are sent from contact@findonlinedegrees.org. Every
                marketing email includes an unsubscribe link. You may opt out at
                any time and your request will be honored within 10 business
                days. Opting out of marketing email does not affect
                transactional or administrative messages related to a specific
                inquiry you have submitted.
              </p>
            </div>
          </div>

          {/* 3. SMS / Text Message Communications */}
          <div data-id="element-1024">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1025">
              3. SMS / TEXT MESSAGE COMMUNICATIONS
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1026">
              <h3 className="font-bold text-lg" data-id="element-1027">How We Collect SMS Consent</h3>
              <p data-id="element-1028">
                When you submit a form on our website, you may expressly consent
                to receive autodialed and/or pre-recorded marketing text
                messages (SMS/MMS) from FindOnlineDegrees.org (operated by Media
                Matchers LLC) and its educational partners at the mobile phone
                number you provide. This consent is obtained at the point of
                form submission and is documented with the date, time, IP
                address, and page URL at which consent was given.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1029">
                How We Use Your Phone Number for SMS
              </h3>
              <p data-id="element-1030">
                If you have provided consent, we may send you text messages for
                purposes including:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1031">
                <li data-id="element-1032">
                  Information about educational programs matching your interests
                </li>
                <li data-id="element-1033">Follow-up communications related to your inquiry</li>
                <li data-id="element-1034">
                  Promotional offers and marketing messages from us or our
                  educational partners
                </li>
              </ul>

              <h3 className="font-bold text-lg mt-6" data-id="element-1035">
                Consent Is Not Required
              </h3>
              <p data-id="element-1036">
                Your consent to receive SMS/MMS messages is never a condition of
                using our services or receiving information. You may choose not
                to provide consent and still access our website and resources.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1037">
                Message Frequency and Rates
              </h3>
              <p data-id="element-1038">
                Message frequency varies based on your interactions and
                preferences. Standard message and data rates may apply depending
                on your carrier and plan.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1039">How to Opt Out</h3>
              <p data-id="element-1040">
                You may revoke your consent and opt out of SMS communications at
                any time by:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1041">
                <li data-id="element-1042">Replying STOP to any text message you receive from us</li>
                <li data-id="element-1043">
                  Contacting us directly using the information in Section 12
                </li>
              </ul>
              <p data-id="element-1044">
                Opt-out requests will be honored promptly. After opting out, you
                may receive a single confirmation message. You will not receive
                further SMS marketing messages unless you provide new consent.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1045">For Assistance</h3>
              <p data-id="element-1046">
                Reply HELP to any text message for support information, or
                contact us directly using the information in Section 12.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1047">
                Third-Party SMS Senders
              </h3>
              <p data-id="element-1048">
                When you consent to receive communications from our educational
                partners, those partners may contact you directly via SMS under
                their own messaging programs and privacy policies. We encourage
                you to review the privacy policies of any educational
                institution or partner that contacts you.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1049">Record Retention</h3>
              <p data-id="element-1050">
                We retain records of SMS consent, including timestamp, IP
                address, form URL, and submitted form data, in accordance with
                our general data retention practices described in Section 6.
              </p>
            </div>
          </div>

          {/* 4. When and With Whom Do We Share */}
          <div data-id="element-1051">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1052">
              4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1053">
              <p data-id="element-1054">We may share information in the following situations:</p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1055">
                <li data-id="element-1056">
                  <strong data-id="element-1057">Business Transfers:</strong> In connection with, or
                  during negotiations of, any merger, sale of company assets,
                  financing, or acquisition.
                </li>
                <li data-id="element-1058">
                  <strong data-id="element-1059">Educational Institutions:</strong> We share your
                  information with schools and educational partners that match
                  your interests.
                </li>
                <li data-id="element-1060">
                  <strong data-id="element-1061">Service Providers:</strong> With third-party vendors,
                  service providers, contractors, or agents who perform services
                  for us.
                </li>
                <li data-id="element-1062">
                  <strong data-id="element-1063">Advertising Partners:</strong> With advertising
                  partners to deliver targeted ads.
                </li>
                <li data-id="element-1064">
                  <strong data-id="element-1065">Legal Requirements:</strong> When required by law or
                  to protect rights, safety, and property.
                </li>
              </ul>
            </div>
          </div>

          {/* 5. Cookies and Tracking */}
          <div data-id="element-1066">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1067">
              5. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1068">
              <p data-id="element-1069">
                Yes, we use cookies and similar tracking technologies to access
                or store information. See our Cookie Notice for more details
                about how we use cookies.
              </p>
            </div>
          </div>

          {/* 6. How Long Do We Keep */}
          <div data-id="element-1070">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1071">
              6. HOW LONG DO WE KEEP YOUR INFORMATION?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1072">
              <p data-id="element-1073">
                We keep your information for as long as necessary to fulfill the
                purposes outlined in this privacy notice unless otherwise
                required by law. When we have no ongoing legitimate business
                need to process your personal information, we will delete or
                anonymize it.
              </p>
            </div>
          </div>

          {/* 7. How Do We Keep Information Safe */}
          <div data-id="element-1074">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1075">
              7. HOW DO WE KEEP YOUR INFORMATION SAFE?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1076">
              <p data-id="element-1077">
                We have implemented appropriate technical and organizational
                security measures designed to protect the security of any
                personal information we process. However, no electronic
                transmission over the internet or information storage technology
                can be guaranteed to be 100% secure.
              </p>
            </div>
          </div>

          {/* 8. Minors */}
          <div data-id="element-1078">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1079">
              8. DO WE COLLECT INFORMATION FROM MINORS?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1080">
              <p data-id="element-1081">
                We do not knowingly solicit data from or market to children
                under 18 years of age. If you become aware that a child has
                provided us with personal information, please contact us. If we
                learn we have collected personal information from a child under
                18, we will delete that information.
              </p>
            </div>
          </div>

          {/* 9. Privacy Rights */}
          <div data-id="element-1082">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1083">
              9. WHAT ARE YOUR PRIVACY RIGHTS?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1084">
              <p data-id="element-1085">
                Depending on your location, you may have the following rights:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1086">
                <li data-id="element-1087">The right to access your personal information</li>
                <li data-id="element-1088">
                  The right to request correction of your personal information
                </li>
                <li data-id="element-1089">
                  The right to request deletion of your personal information
                </li>
                <li data-id="element-1090">The right to opt out of marketing communications</li>
                <li data-id="element-1091">
                  The right to opt out of the sale or sharing of personal
                  information (where applicable)
                </li>
              </ul>
              <p className="mt-4" data-id="element-1092">
                To exercise these rights, please contact us using the
                information in Section 12.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1093">
                Opt Out of Marketing Email
              </h3>
              <p data-id="element-1094">
                To opt out of marketing email: Click the unsubscribe link in any
                marketing email you receive from us, or contact us at the
                address in Section 12. Requests are processed within 10 business
                days.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1095">
                California Residents — Opt Out of Sale/Sharing
              </h3>
              <p data-id="element-1096">
                California residents — opt out of sale/sharing of personal
                information: To exercise your right to opt out under CCPA/CPRA,
                contact us at the address in Section 12 with the subject line
                "California Privacy Opt-Out." We will process your request
                within 15 business days.
              </p>
            </div>
          </div>

          {/* 10. Do Not Track */}
          <div data-id="element-1097">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1098">
              10. CONTROLS FOR DO-NOT-TRACK FEATURES
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1099">
              <p data-id="element-1100">
                Most web browsers and some mobile operating systems include a
                Do-Not-Track ("DNT") feature or setting you can activate to
                signal your privacy preference. At this stage, no uniform
                technology standard for recognizing and implementing DNT signals
                has been finalized.
              </p>
            </div>
          </div>

          {/* 11. Updates */}
          <div data-id="element-1101">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1102">
              11. DO WE MAKE UPDATES TO THIS NOTICE?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1103">
              <p data-id="element-1104">
                Yes, we will update this notice as necessary to stay compliant
                with relevant laws. The updated version will be indicated by an
                updated "Last updated" date at the top of this privacy notice.
              </p>
            </div>
          </div>

          {/* 12. Contact Us */}
          <div data-id="element-1105">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1106">
              12. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1107">
              <p data-id="element-1108">
                If you have questions or comments about this notice, please
                contact us at:
              </p>
              <p className="font-semibold" data-id="element-1109">Media Matchers LLC</p>
              <p data-id="element-1110">
                1865 Chestnut Street
                <br data-id="element-1111" />
                San Francisco, CA 94123
              </p>
              <p data-id="element-1112">
                Email:{' '}
                <a href="mailto:contact@findonlinedegrees.org" className="text-[#3b82f6] underline hover:text-blue-800" data-id="element-1113">
                  contact@findonlinedegrees.org
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>;
}